import React, { useState, useEffect, useMemo } from 'react';
import PlotlyChart from '../../components/charts/PlotlyChart';
import { HEATMAP_RETURNS } from '../../components/charts/chartTheme';
import { hfService } from '../../services/hfService';
import { parseDfJson, formatPct } from '../../utils/formatters';
import { getFundColor } from '../../utils/colors';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

const CalendarPage = ({ analyticsParams, fundList, focusFund, cachedFetch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFund, setSelectedFund] = useState('');

  useEffect(() => {
    if (!analyticsParams?.dataset_id || !analyticsParams?.funds?.length) return;

    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchFn = () => hfService.getCalendar(analyticsParams);
        const res = cachedFetch ? await cachedFetch('calendar', fetchFn) : await fetchFn();
        if (!cancelled) {
          setData(res.data);
          // Default to focus fund if available, else first fund
          const monthlyFunds = Object.keys(res.data.monthly || {});
          if (monthlyFunds.length > 0) {
            if (focusFund && monthlyFunds.includes(focusFund)) {
              setSelectedFund(focusFund);
            } else if (!selectedFund) {
              setSelectedFund(monthlyFunds[0]);
            }
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsParams, focusFund]);

  /* --- Annual returns heatmap + table --- */
  const { heatmapData, heatmapLayout, yearlyDf, monthlyHeatmapData, monthlyHeatmapLayout, spxHeatmapData, spxHeatmapLayout } =
    useMemo(() => {
      if (!data) return {};

      const { yearly, monthly, colors } = data;
      const yDfRaw = parseDfJson(yearly);

      /* Filter to years >= focus fund's first year */
      const focusCol = focusFund ? yDfRaw.columns.indexOf(focusFund) : -1;
      let filteredIndices;
      if (focusCol >= 0) {
        // Find focus fund's earliest year with data
        let focusFirstIdx = -1;
        for (let i = 0; i < yDfRaw.index.length; i++) {
          if (yDfRaw.data[i]?.[focusCol] != null) {
            focusFirstIdx = i;
            break;
          }
        }
        // Include all years from focus first year onwards
        filteredIndices = focusFirstIdx >= 0
          ? yDfRaw.index.map((_, i) => i).filter(i => i >= focusFirstIdx)
          : yDfRaw.index.map((_, i) => i);
      } else {
        filteredIndices = yDfRaw.index.map((_, i) => i);
      }
      const yDf = {
        index: filteredIndices.map(i => yDfRaw.index[i]),
        columns: yDfRaw.columns,
        data: filteredIndices.map(i => yDfRaw.data[i]),
      };

      /* Annual heatmap: rows = years, columns = funds */
      const zValues = yDf.data;
      const years = yDf.index.map(d => {
        const v = typeof d === 'string' ? d.substring(0, 4) : String(d);
        return v;
      });
      const funds = yDf.columns;
      const displayFunds = isMobile ? funds.map(f => f.substring(0, 4)) : funds;

      // Build annotation text
      const annotations = [];
      for (let i = 0; i < years.length; i++) {
        for (let j = 0; j < funds.length; j++) {
          const val = zValues[i]?.[j];
          annotations.push({
            x: displayFunds[j],
            y: years[i],
            text: val != null ? formatPct(val, 1) : '',
            showarrow: false,
            font: {
              size: isMobile ? 7 : 11,
              color: val != null && Math.abs(val) > 0.15 ? '#fff' : '#333',
            },
          });
        }
      }

      const heatmapData = [
        {
          z: zValues,
          x: displayFunds,
          y: years,
          type: 'heatmap',
          colorscale: HEATMAP_RETURNS,
          zmid: 0,
          zmin: -0.3,
          zmax: 0.3,
          hovertemplate: '%{y} - %{x}: %{z:.2%}<extra></extra>',
          colorbar: { tickformat: '.0%', title: 'Return' },
        },
      ];

      const heatmapLayout = {
        title: 'Annual Returns Heatmap',
        xaxis: {
          side: 'top',
          tickangle: -45,
          gridcolor: '#eee',
          zerolinecolor: '#ddd',
          tickformat: '',
        },
        yaxis: {
          autorange: 'reversed',
          gridcolor: '#eee',
          zerolinecolor: '#ddd',
          tickformat: '',
        },
        annotations,
        margin: { l: 60, r: 15, t: 80, b: 20 },
        hovermode: 'closest',
      };

      /* Monthly heatmap for selected fund */
      let monthlyHeatmapData = null;
      let monthlyHeatmapLayout = null;

      /* Set of valid years based on focus fund filtering */
      const validYearSet = new Set(years);

      if (selectedFund && monthly && monthly[selectedFund]) {
        const mDfRaw = parseDfJson(monthly[selectedFund]);
        // mDfRaw: index = years, columns = months (1..12), data = values
        // Filter to only years that exist in the focus-fund-filtered annual data
        const mFilteredIndices = [];
        for (let i = 0; i < mDfRaw.index.length; i++) {
          const yr = typeof mDfRaw.index[i] === 'string' ? mDfRaw.index[i].substring(0, 4) : String(mDfRaw.index[i]);
          if (validYearSet.has(yr)) mFilteredIndices.push(i);
        }
        const mYears = mFilteredIndices.map(i => {
          const d = mDfRaw.index[i];
          const v = typeof d === 'string' ? d.substring(0, 4) : String(d);
          return v;
        });
        const mMonths = mDfRaw.columns.map((c, i) => MONTHS[i] || c);
        const mZ = mFilteredIndices.map(i => mDfRaw.data[i]);

        // Month annotations
        const mAnnotations = [];
        for (let i = 0; i < mYears.length; i++) {
          for (let j = 0; j < mMonths.length; j++) {
            const val = mZ[i]?.[j];
            mAnnotations.push({
              x: mMonths[j],
              y: mYears[i],
              text: val != null ? formatPct(val, 1) : '',
              showarrow: false,
              font: {
                size: 10,
                color: val != null && Math.abs(val) > 0.05 ? '#fff' : '#333',
              },
            });
          }
        }

        monthlyHeatmapData = [
          {
            z: mZ,
            x: mMonths,
            y: mYears,
            type: 'heatmap',
            colorscale: HEATMAP_RETURNS,
            zmid: 0,
            zmin: -0.1,
            zmax: 0.1,
            hovertemplate: '%{y} %{x}: %{z:.2%}<extra></extra>',
            colorbar: { tickformat: '.0%', title: 'Return' },
          },
        ];

        monthlyHeatmapLayout = {
          title: `${selectedFund} - Monthly Returns`,
          xaxis: {
            side: 'top',
            gridcolor: '#eee',
            zerolinecolor: '#ddd',
            tickformat: '',
          },
          yaxis: {
            autorange: 'reversed',
            gridcolor: '#eee',
            zerolinecolor: '#ddd',
            tickformat: '',
          },
          annotations: mAnnotations,
          margin: { l: 60, r: 15, t: 80, b: 20 },
          hovermode: 'closest',
        };
      }

      // SPX monthly heatmap — same year filter as fund heatmap
      let spxHeatmapData = null;
      let spxHeatmapLayout = null;
      if (data.spx_monthly) {
        const sDfRaw = parseDfJson(data.spx_monthly);
        const sFilteredIndices = [];
        for (let i = 0; i < sDfRaw.index.length; i++) {
          const yr = typeof sDfRaw.index[i] === 'string' ? sDfRaw.index[i].substring(0, 4) : String(sDfRaw.index[i]);
          if (validYearSet.has(yr)) sFilteredIndices.push(i);
        }
        const sYears = sFilteredIndices.map(i => {
          const d = sDfRaw.index[i];
          return typeof d === 'string' ? d.substring(0, 4) : String(d);
        });
        const sMonths = sDfRaw.columns.map((c, i) => MONTHS[i] || c);
        const sZ = sFilteredIndices.map(i => sDfRaw.data[i]);

        const sAnnotations = [];
        for (let i = 0; i < sYears.length; i++) {
          for (let j = 0; j < sMonths.length; j++) {
            const val = sZ[i]?.[j];
            sAnnotations.push({
              x: sMonths[j], y: sYears[i],
              text: val != null ? formatPct(val, 1) : '',
              showarrow: false,
              font: { size: 10, color: val != null && Math.abs(val) > 0.05 ? '#fff' : '#333' },
            });
          }
        }

        spxHeatmapData = [{
          z: sZ, x: sMonths, y: sYears,
          type: 'heatmap', colorscale: HEATMAP_RETURNS,
          zmid: 0, zmin: -0.1, zmax: 0.1,
          hovertemplate: '%{y} %{x}: %{z:.2%}<extra></extra>',
          colorbar: { tickformat: '.0%', title: 'Return' },
        }];
        spxHeatmapLayout = {
          title: 'SPX Index — Monthly Returns',
          xaxis: { side: 'top', gridcolor: '#eee', zerolinecolor: '#ddd', tickformat: '' },
          yaxis: { autorange: 'reversed', gridcolor: '#eee', zerolinecolor: '#ddd', tickformat: '' },
          annotations: sAnnotations,
          margin: { l: 60, r: 15, t: 80, b: 20 },
          hovermode: 'closest',
        };
      }

      return {
        heatmapData,
        heatmapLayout,
        yearlyDf: yDf,
        monthlyHeatmapData,
        monthlyHeatmapLayout,
        spxHeatmapData,
        spxHeatmapLayout,
      };
    }, [data, selectedFund, focusFund]);

  if (!analyticsParams?.dataset_id) {
    return (
      <div className="rounded-lg border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-700 dark:text-blue-400">
        Select a dataset and funds to view calendar returns.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-gold" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Calculating calendar returns...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const monthlyFunds = Object.keys(data.monthly || {});
  const selectCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none';

  return (
    <div>
      {/* Annual returns heatmap */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
        <div className="overflow-x-auto">
          <PlotlyChart
            data={heatmapData}
            interactive
            layout={heatmapLayout}
            style={{
              minHeight: isMobile
                ? Math.max(250, (yearlyDf?.index?.length || 5) * 25 + 80)
                : Math.max(350, (yearlyDf?.index?.length || 5) * 30 + 100),
            }}
          />
        </div>
      </div>

      {/* Monthly heatmap with fund selector */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">Monthly Returns Heatmap</span>
          <select
            className={`${selectCls} max-w-xs`}
            value={selectedFund}
            onChange={e => setSelectedFund(e.target.value)}
          >
            {monthlyFunds.map(fund => (
              <option key={fund} value={fund}>
                {fund}
              </option>
            ))}
          </select>
        </div>
        <div className="p-4 md:p-5">
          {monthlyHeatmapData ? (
            <div className="overflow-x-auto">
              <PlotlyChart
                data={monthlyHeatmapData}
                interactive
                layout={monthlyHeatmapLayout}
                style={{
                  minHeight: Math.max(
                    400,
                    (data.monthly?.[selectedFund]?.index?.length || 5) * 35 + 120
                  ),
                }}
              />
            </div>
          ) : (
            <p className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
              Select a fund to view monthly returns.
            </p>
          )}
        </div>
      </div>

      {/* SPX Monthly Heatmap */}
      {spxHeatmapData && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">SPX Index — Monthly Returns</span>
          </div>
          <div className="p-4 md:p-5">
            <div className="overflow-x-auto">
              <PlotlyChart data={spxHeatmapData} interactive layout={spxHeatmapLayout}
                style={{ minHeight: Math.max(400, (spxHeatmapData[0]?.y?.length || 5) * 35 + 120) }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
